import { IconButton, Tag } from '@ads/components-react';
import { LanguageIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CountryViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface CountryTableProps {
  /** Página atual de países já fatiada para exibição. */
  readonly data: readonly CountryViewModel[];
  readonly isLoading: boolean;
  /** Página atual (1-indexed). */
  readonly currentPage: number;
  /** Total de páginas (derivado client-side do total filtrado). */
  readonly totalPages: number;
  /** Total de registros filtrados (para o texto "Exibindo X–Y de Z"). */
  readonly totalCount: number;
  /** Itens por página. */
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  /** Acionado ao gerenciar os idiomas do país (navega para a tela de idiomas). */
  readonly onManageLangs: (country: CountryViewModel) => void;
  readonly onEdit: (country: CountryViewModel) => void;
  readonly onDelete: (country: CountryViewModel) => void;
}

/**
 * Tabela de Países. Renderiza Código, Nome, status (Tag) e ações (editar/excluir),
 * delegando loading/empty/paginação ao `DataTable` e habilitando ordenação
 * client-side (na página atual). Fiel ao frame *Screen: Países (Listagem)* de
 * `design/location.pen`.
 *
 * TODO: a paginação é client-side (a lista chega completa da API). Quando o
 * backend paginar `GET /api/v1/location/countries`, usar `totalPages`/`totalCount`
 * do envelope da resposta em vez dos derivados na página. Ver `COUNTRY_PAGE_SIZE`.
 */
export function CountryTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onManageLangs,
  onEdit,
  onDelete,
}: CountryTableProps) {
  const { t } = useTranslation('location');

  const columns = useMemo<ColumnDef<CountryViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'code',
        header: t('countries.table.columns.code'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        accessorKey: 'name',
        header: t('countries.table.columns.name'),
        cell: info => (
          <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
            {(info.getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        id: 'active',
        accessorKey: 'active',
        header: t('countries.table.columns.active'),
        cell: ({ row }) => {
          const active = row.original.active ?? false;
          return (
            <Tag
              label={t(active ? 'countries.table.statusActive' : 'countries.table.statusInactive')}
              size="md"
              status={active ? 'positive' : 'default'}
              custom={{ text: 'text-nowrap' }}
            />
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('countries.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-quark">
            <IconButton
              icon={LanguageIcon}
              ariaLabel={t('countries.table.manageLangs')}
              hierarchy="tertiary"
              size="md"
              onClick={() => onManageLangs(row.original)}
            />
            <IconButton
              icon={PencilSquareIcon}
              ariaLabel={t('countries.table.edit')}
              hierarchy="tertiary"
              size="md"
              onClick={() => onEdit(row.original)}
            />
            <IconButton
              icon={TrashIcon}
              ariaLabel={t('countries.table.delete')}
              hierarchy="tertiary"
              className="!text-base-danger-600"
              size="md"
              onClick={() => onDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [t, onManageLangs, onEdit, onDelete],
  );

  return (
    <DataTable
      data={data as CountryViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('countries.table.empty')}
      emptyStateDescription={t('countries.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
      enableSorting
    />
  );
}
